import { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ClipboardList, Mail, MessageCircle, ShoppingBag, X } from 'lucide-react';
import { useContactForm } from '../context/ContactFormContext';

const TELEGRAM_URL = 'https://t.me/RedLevelCrypto';
const EMAIL_URL = 'mailto:redlevelsupport@icloud.com';

const BuyCourseModal = () => {
  const { t } = useTranslation();
  const {
    isBuyOpen,
    closeBuy,
    buyCourseName,
    buyInstanceId,
    open,
    openCallback,
  } = useContactForm();

  useEffect(() => {
    if (isBuyOpen) {
      document.body.style.overflow = 'hidden';
    } else if (!document.querySelector('[data-modal-lock]')) {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isBuyOpen, buyInstanceId]);

  return (
    <AnimatePresence>
      {isBuyOpen && (
        <>
          <motion.div
            key="buy-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeBuy}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm"
          />

          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={closeBuy}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 16 }}
              onClick={(event) => event.stopPropagation()}
              className="w-full max-w-lg"
              data-modal-lock
            >
              <div className="relative bg-crypto-card border border-crypto-red/30 rounded-3xl p-6 sm:p-8">
                <button
                  onClick={closeBuy}
                  className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white transition-colors"
                  aria-label={t('buyModal.close')}
                >
                  <X className="w-5 h-5" />
                </button>

                <div className="w-14 h-14 bg-crypto-red/10 rounded-2xl flex items-center justify-center mb-5">
                  <ShoppingBag className="w-7 h-7 text-crypto-red" />
                </div>

                <h3 className="text-2xl font-bold text-white mb-2">
                  {t('buyModal.title')}
                  {buyCourseName ? (
                    <span className="block text-lg font-semibold text-crypto-red mt-1">
                      {buyCourseName}
                    </span>
                  ) : null}
                </h3>
                <p className="text-gray-400 mb-6 leading-relaxed">{t('buyModal.text')}</p>

                <div className="space-y-3">
                  <a
                    href={TELEGRAM_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full px-4 py-3.5 rounded-xl bg-[#229ED9]/15 border border-[#229ED9]/40 text-white font-medium hover:bg-[#229ED9]/25 transition-colors"
                  >
                    <MessageCircle className="w-4 h-4" />
                    {t('buyModal.writeTelegram')}
                  </a>
                  <a
                    href={EMAIL_URL}
                    className="flex items-center justify-center gap-2 w-full px-4 py-3.5 rounded-xl bg-crypto-dark border border-crypto-red/20 text-white font-medium hover:border-crypto-red/50 transition-colors"
                  >
                    <Mail className="w-4 h-4 text-crypto-red" />
                    {t('buyModal.writeEmail')}
                  </a>
                  <button
                    onClick={() => openCallback(buyCourseName)}
                    className="flex items-center justify-center gap-2 w-full px-4 py-3.5 rounded-xl bg-crypto-red hover:bg-crypto-redDark text-white font-bold transition-colors"
                  >
                    {t('buyModal.callback')}
                  </button>
                </div>

                <div className="mt-6 pt-6 border-t border-crypto-red/10">
                  <button
                    onClick={() => open(buyCourseName)}
                    className="flex items-center justify-center gap-2 w-full px-4 py-3.5 rounded-xl border border-crypto-red/30 text-gray-200 hover:text-white hover:border-crypto-red/60 hover:bg-crypto-red/10 transition-colors font-medium text-center"
                  >
                    <ClipboardList className="w-4 h-4 text-crypto-red shrink-0" />
                    <span>{t('buyModal.takeTest')}</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default BuyCourseModal;
